import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";
import { App } from "./App.js";
import { ApiError } from "./api.js";
import { observeVitals, sendClientEvent, currentPage, errorMessage } from "./telemetry.js";
import { isChunkLoadError } from "./ui/ErrorBoundary.js";
import { announceNewVersion } from "./api.js";

// 想定外の例外（画面の外で起きたもの）。API のエラーはサーバ側に記録があるので送らない。
addEventListener("error", (e) => {
  if (isChunkLoadError(e.error ?? e.message)) { if (navigator.onLine !== false) announceNewVersion(); return; }
  sendClientEvent({ kind: "error", page: currentPage(), message: errorMessage(e.error ?? e.message) });
});
addEventListener("unhandledrejection", (e) => {
  if (e.reason instanceof ApiError) return;
  if (isChunkLoadError(e.reason)) { if (navigator.onLine !== false) announceNewVersion(); return; }
  sendClientEvent({ kind: "error", page: currentPage(), message: errorMessage(e.reason) });
});
// Vite: 分割したファイルの先読みに失敗した（デプロイで古いファイルが消えた）
addEventListener("vite:preloadError", () => { if (navigator.onLine !== false) announceNewVersion(); });

observeVitals();

// 書体（約 1.3MB × 2）は初回表示の後に読む。読み終わるまでは Hiragino / Noto Sans JP で表示する
// （最初の表示と通信を書体のダウンロードと取り合わない。2回目以降はキャッシュから即座に使われる）
const loadFonts = () => { void import("./fonts.js"); };
if (document.readyState === "complete") setTimeout(loadFonts, 0);
else addEventListener("load", () => setTimeout(loadFonts, 0), { once: true });


// スマホのキーボード（送信ボタンの帯をキーボードの上に出し、下部タブを隠すため）。
// iOS はキーボードが表示領域に重なる（visualViewport が縮む）→ その高さを --kb に。
// Android（interactive-widget=resizes-content）は画面そのものが縮む → 縮み方で判定する。
const vv = window.visualViewport;
if (vv) {
  let tallest = window.innerHeight;
  let lastWidth = window.innerWidth;
  let lastKb = "";
  let lastOpen: boolean | null = null;
  let frame = 0;
  // visualViewport の scroll はスクロール中に何度も来る。1フレームに1回だけ測り、値が変わったときだけ書く
  // （:root の変数を書くと文書全体のスタイルの再計算になるため）
  const measureKb = () => {
    frame = 0;
    if (window.innerWidth !== lastWidth) { lastWidth = window.innerWidth; tallest = window.innerHeight; }   // 向きが変わった
    tallest = Math.max(tallest, window.innerHeight);
    const overlay = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    const shrunk = window.innerHeight < tallest * 0.75;
    const kb = `${Math.round(overlay)}px`;
    const open = overlay > 120 || shrunk;
    const root = document.documentElement;
    if (kb !== lastKb) { lastKb = kb; root.style.setProperty("--kb", kb); }
    if (open !== lastOpen) { lastOpen = open; root.toggleAttribute("data-kb", open); }
  };
  const update = () => { if (!frame) frame = requestAnimationFrame(measureKb); };
  vv.addEventListener("resize", update);
  vv.addEventListener("scroll", update, { passive: true });
  addEventListener("resize", update);
}

// L-12: 画面を移ったときに見出しへ移すフォーカスの枠は、キーボードで操作しているときだけ見せる
// （マウス・タッチで移ったときに見出しが入力欄のように見えないように）
{
  const root = document.documentElement;
  addEventListener("pointerdown", () => { if (!root.hasAttribute("data-pointer")) root.setAttribute("data-pointer", ""); }, { capture: true, passive: true });
  addEventListener("keydown", (e) => { if (e.key === "Tab" || e.key.startsWith("Arrow")) root.removeAttribute("data-pointer"); }, { capture: true });
}

// スクロールした後だけ、上部の見出し帯を半透明の面にする。スクロールのイベントは使わず、
// 先頭に置いた小さな目印が画面から外れたかを IntersectionObserver で見る（値が変わったときだけ書く）
{
  const root = document.documentElement;
  const mark = document.createElement("div");
  mark.setAttribute("aria-hidden", "true");
  mark.style.cssText = "position:absolute;top:0;left:0;width:1px;height:4px;pointer-events:none";
  document.body.prepend(mark);
  if ("IntersectionObserver" in window) {
    new IntersectionObserver(([e]) => { root.toggleAttribute("data-scrolled", !e!.isIntersecting); }).observe(mark);
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
